import view from "../Enums";

/**
 * Checks if obj is a valid non empty string
 */
export const isEmptyString = obj => obj == null || obj === undefined || (typeof obj == 'string' && obj.trim() === '');

export const notIsEmptyString = obj => !isEmptyString(obj);

/**
 * Checks if obj is a valid non empty list
 */
export const isEmptyList = obj => !Array.isArray(obj) || (Array.isArray(obj) && obj.length === 0);

export const notIsEmptyList = obj => !isEmptyList(obj);

export const isEmptyObject = obj => obj == null || typeof obj !== 'object' || Object.keys(obj).length === 0;

export const notIsEmptyObject = obj => !isEmptyObject(obj);

export const isValidNumber = obj => obj != null && !isNaN(parseFloat(obj)) && !isNaN(Number(obj));

export const notIsValidNumber = obj => !isValidNumber(obj);

/**
 * @param {Object}   obj               The Obj in which the path is to be accessed
 * @param {String}   accumulatedPath   The path to the value to be accessed
 * @param {*}        defaultValue      Default to be returned if path doesn't exist or if the final value doesn't pass validator
 * @param {Function} validator         returns the value at the end of path if validator returns true for that value or else returns defaultValue
 * @return {*}                         The value at the end of the path or default if the path doesn't exist
 *
 * Example (x, 'key1.key2.key3', y) - Returns x.key1.key2.key3 if avaialble and y if unavailable
 */
export const path = (obj, accumulatedPath, defaultValue, validator) => {
    if (obj == null || typeof obj != 'object' || (typeof accumulatedPath != 'string' && typeof accumulatedPath != 'number')) return defaultValue;

    const path = String(accumulatedPath).split('.');
    let tempObj = obj;

    for (let accessor of path) {
        if (tempObj == null || typeof tempObj != 'object') return defaultValue;
        else if (accessor in tempObj) tempObj = tempObj[accessor];
        else return defaultValue;
    }

    if (tempObj == null) return defaultValue;
    else if (typeof validator != 'function') return tempObj;
    else return validator(tempObj) ? tempObj : defaultValue;
};

/**
 * @param {Object} common            The Common Styles for all View modes
 * @param {Object} desktop           The Styles specific to Desktop or a view to copy styles from
 * @param {Object} tab               The Styles specific to Tab or a view to copy styles from
 * @param {Object} mobile            The Styles specific to Mobile or a view to copy styles from
 * @return {*}                       An Object with the View Modes as Keys and styles as their values
 */
export const makeStyle = (common, desktop, tab, mobile) => {
    var c = Array.isArray(common) ? merge(common) : common;
    var d = Array.isArray(desktop) ? merge(desktop) : desktop;
    var t = Array.isArray(tab) ? merge(tab) : tab;
    var m = Array.isArray(mobile) ? merge(mobile) : mobile;

    const map = { [view.DESKTOP]: d, [view.TAB]: t, [view.MOBILE]: m };
    return {
        [view.DESKTOP]: merge(c, typeof d === 'object' ? d : typeof map[d] === 'object' ? map[d] : {}),
        [view.TAB]: merge(c, typeof t === 'object' ? t : typeof map[t] === 'object' ? map[t] : {}),
        [view.MOBILE]: merge(c, typeof m === 'object' ? m : typeof map[m] === 'object' ? map[m] : {}),
    }
};

export const createAction = type => payload => ({ type, payload });

export const buildUrl = (appendSlash, ...args) => appendSlash ? "/" + args.join("/") : args.join("/");

export const createUrl = (paths,appendSlash,params) => {
    let url='';
    if(appendSlash) url += '/';
    if(!isEmptyList(paths)){
      paths.forEach(path => {
        if(notIsEmptyString(path))
          url+=(path+'/');
      });
    }
    if(!isEmptyObject(params))url += '?'  + Object.entries(params).map( ([k,v]) => k + '=' +v).join("&");
    return url;
};

export const getQueryString = myQuery => {
    if(isEmptyObject(myQuery))return '';
    let str = [];
    for (let key in myQuery) {
        if (myQuery.hasOwnProperty(key)) {
            str.push(
                encodeURIComponent(key) + '=' + encodeURIComponent(myQuery[key])
            );
        }
    }
    return str.join('&');
};

export const getQueryParams = url => {
    if (isEmptyString(url)) url = window.location.search;

    const params = {};
    url.substr(url.indexOf('?') + 1).split("&").forEach(part => {
        const items = part.split("=");
        params[items[0]] = decodeURIComponent(items[1]);
    });
    return params;
};

export const getCookie = key => {
    const name = key + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1);
        if (c.indexOf(name) === 0) return c.substring(name.length, c.length);
    }
    return "";
};

export const setCookie = (key, value, expiryDays, staging) => {
    let d = new Date();
    d.setTime(d.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
    const expires = "expires=" + d.toUTCString();
    // document.cookie = key + "=" + value + ";" + expires + `; domain=.oyorooms.${staging ? 'ms' : 'com'}; path=/;`;
    document.cookie = key + "=" + value + ";" + expires + `; path=/;`;
};

/**
 * @param {Object | Object[]} obj      The Object or Array of Objects to operate on
 * @param {String []} desktop          List of keys to be removed in the above param
 * @return {*}                         Resultant object or array with keys removed (Doesn't mutate original param)
 */
export const removeKeys = (obj, keys) => {
    if (obj == null || isEmptyList(keys)) return obj;
    if (Array.isArray(obj)) {
        return [].concat(obj.map(_ => removeKeys(_, keys)));
    }
    if (typeof obj == 'object') {
        let result = { ...obj };
        keys.forEach(key => delete result[key]);
        return result;
    }
    return obj;
};

/**
 * @params              List of items where each item is either an Object or an Array of Objects2
 * @return {Object}     Resultant object is prepared by merging all the objects and arrays of objects in the params
 */
export const merge = (...args) => isEmptyList(args) ? {} :
    args.reduce((accu, curr) => {
        if (!isEmptyList(curr)) return Object.assign(accu, merge(...curr));
        if (typeof curr === 'object') return Object.assign(accu, curr);
        return accu;
    }, {});

export const deepCheck = (obj1, obj2) => {
    if (obj1 === null && obj2 === null) return true;

    if (typeof obj1 != typeof obj2 || (typeof obj1 != 'object' && typeof obj1 != 'function' && obj1 != obj2))
        return false;

    if (typeof obj1 == 'function') return true;

    if (typeof obj1 != 'object' && obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) return false;

    if (Object.keys(obj1).length !== Object.keys(obj2).length)
        return false;

    let flag = true, keys1 = Object.keys(obj1), keys2 = Object.keys(obj2), length = Object.keys(obj1).length;
    for (let i = 0; i < length; i++) {
        if (keys1[i] !== keys2[i] || !deepCheck(obj1[keys1[i]], obj2[keys2[i]]))
            flag = false;
    }
    return flag;
};

export const mod = (a, b) => ((a % b) + b) % b;

export const isClientSide = () => typeof window !== 'undefined';

export const parseJsonString = async (content_json_key) => {
    try {
        const { guc } = await import('../services/Localisation');
        const data = JSON.parse(guc(content_json_key));
        return isEmptyObject(data) ? { isValid: false } : { isValid: true, data };
    } catch (_) {
        console.log(`Error parsing ${content_json_key}`);
        return { isValid: false };
    }
};

export const validateMobileNumber = mobileNumber => notIsEmptyString(mobileNumber) && mobileNumber.length >= 5 && mobileNumber.length <= 16 && isValidNumber(mobileNumber.lastIndexOf('+') === 0 ? mobileNumber.substring(1) : mobileNumber);

export const checkMobileAndTabletBrowser = () => {
    const userAgent = (navigator && navigator.userAgent.toLowerCase()) || '';
    const check =
        /(android|bb\d+|meego).+mobile|android 3.0|xoom|sch-i800|playbook|tablet|kindle|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino|vodafone|wap|android|blackBerry|tablet|mobile|iphone|ipad|ipod|opera mini|iemobile/i.test(
            userAgent
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
            userAgent.substr(0, 4)
        );
    return check;
};
